import { useEffect } from 'react';

import {
  pushAsEcommerceToDataLayer,
  useGetAddPaymentInfoEvent,
  useGetPurchaseEvent,
  useGetBeginCheckoutEvent,
  useGetAddShippingInfoEvent,
  useGetCheckoutSignInEvent,
  useGetProductEventData
} from './useGTMAnalyticsHelper';
import { useSessionContext } from '../../contexts/sessionProvider/SessionProvider';
import { UseCommonEventProps } from './types';
import { pushToDataLayer } from '../Analytics/useAnalyticsHelper';
import { GTM4_ANALYTICS_CONSTANTS } from '../../constants';

export const useCommonEvent = ({
  ecommerceEvent, shouldPushEvent
}: UseCommonEventProps): void => {
  useEffect(() => {
    if (shouldPushEvent && ecommerceEvent?.items?.length > 0) {
      pushAsEcommerceToDataLayer({ ...ecommerceEvent });
    }
  }, [JSON.stringify(ecommerceEvent), shouldPushEvent]);
};

export const useBeginCheckoutEventGA4 = (isReady: boolean): void => {
  const ecommerceEvent = useGetBeginCheckoutEvent();
  useCommonEvent({
    shouldPushEvent: isReady,
    ecommerceEvent
  });
};

export const useAddShippingInfoEventGA4 = (isReady: boolean, isNewUser: boolean): void => {
  const ecommerceEvent = useGetAddShippingInfoEvent(isNewUser);
  useCommonEvent({
    shouldPushEvent: isReady,
    ecommerceEvent
  });
};

export const useCheckoutSignInEventGA4 = (isReady: boolean, isNewUser: boolean): void => {
  const ecommerceEvent = useGetCheckoutSignInEvent(isNewUser);
  useCommonEvent({
    shouldPushEvent: isReady,
    ecommerceEvent
  });
};

export const useAddPaymentInfoEventGA4 = (): void => {
  const ecommerceEvent = useGetAddPaymentInfoEvent();
  useCommonEvent({
    ecommerceEvent,
    shouldPushEvent: true
  });
};

export const usePurchaseEventGA4 = (): void => {
  const { orderNumber } = useSessionContext();
  const purchaseEvent = useGetPurchaseEvent();

  useEffect(() => {
    if (orderNumber &&
      purchaseEvent.items.length > 0) {
      pushAsEcommerceToDataLayer(purchaseEvent);
    }
  }, [
    JSON.stringify(purchaseEvent),
    orderNumber
  ]);
}

export const usePartnerSignupStartEventGA4 = (isReady: boolean): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    if (isReady) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.PARTNER_SIGN_UP_START
      });
    }
  }, [isReady, JSON.stringify(productEventData)]);
};

export const usePartnerSignupCompleteEventGA4 = (): void => {
  const productEventData = useGetProductEventData();
  const { orderNumber, paymentMethod } = useSessionContext();
  useEffect(() => {
    if (orderNumber) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.PARTNER_SIGN_UP_COMPLETE,
        product_transaction_id: orderNumber,
        product_payment_type: paymentMethod
      })
    }
  }, [orderNumber, JSON.stringify(productEventData), paymentMethod]);
};

export const usePartnerSignupAccountInfoEventGA4 = (isReady: boolean): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    if (isReady) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.PARTNER_SIGN_UP_ACCOUNT_INFO
      });
    }
  }, [JSON.stringify(productEventData), isReady]);

};

export const usePartnerSignupPaymentInfoEventGA4 = (): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    pushToDataLayer({
      ...productEventData,
      event: GTM4_ANALYTICS_CONSTANTS.PARTNER_SIGN_UP_PAYMENT_INFO
    });
  }, [JSON.stringify(productEventData)]);

};

export const useQuoteAcceptStartEventGA4 = (isReady: boolean): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    if (isReady) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.QUOTE_ACCEPT_START
      });
    }
  }, [isReady, JSON.stringify(productEventData)]);
};

export const useQuoteAcceptAccountInfoEventGA4 = (isReady: boolean): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    if (isReady) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.QUOTE_ACCEPT_ACCOUNT_INFO
      });
    }
  }, [JSON.stringify(productEventData), isReady]);

};

export const useQuoteAcceptPaymentInfoEventGA4 = (): void => {
  const productEventData = useGetProductEventData();
  useEffect(() => {
    pushToDataLayer({
      ...productEventData,
      event: GTM4_ANALYTICS_CONSTANTS.QUOTE_ACCEPT_PAYMENT_INFO
    });
  }, [JSON.stringify(productEventData)]);

};

export const useQuoteAcceptCompleteEventGA4 = (): void => {
  const productEventData = useGetProductEventData();
  const { orderNumber, paymentMethod } = useSessionContext();
  useEffect(() => {
    if (orderNumber) {
      pushToDataLayer({
        ...productEventData,
        event: GTM4_ANALYTICS_CONSTANTS.QUOTE_ACCEPT_COMPLETE,
        product_transaction_id: orderNumber,
        product_payment_type: paymentMethod
      })
    }
  }, [orderNumber, JSON.stringify(productEventData), paymentMethod]);
};

export const trackClickEvent = (event: string, attributes: Record<string, string>): void => {
  if (Object.keys(attributes).length) {
    pushToDataLayer({
      event,
      ...attributes
    });
  }
};

export const trackEvent = (event: string, attributes: Record<string, string>): void => {
  if (Object.keys(attributes).length) {
    pushToDataLayer({
      event,
      ...attributes
    });
  }
};

